import { useCallback, useEffect, useMemo } from "react";

import {
  resolveEffectiveAccountUserIds,
  resolveLoggedInUserId,
} from "./stakeholderPortfolioScopeUtils";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  buildRelationshipAccountsRequestKey,
  fetchRelationshipAssessmentAccounts,
  selectAllStakeholderUserIds,
} from "../../Store/Reducers/RelationshipSlice";
import {
  getFromLocalStorage,
  STORAGE_KEYS,
} from "../../Utils/Helper/LocalStorage";

export function useRelationshipAccountsFetch() {
  const dispatch = useAppDispatch();
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const loggedInUserId = resolveLoggedInUserId(loggedInUser);

  const {
    assessmentStakeholders,
    assessmentStakeholdersLoading,
    relationshipAccounts,
    relationshipAccountsLoading,
    relationshipAccountsUserIdsKey,
    selectedStakeholderUserIds,
    viewMyPortfolio,
    error,
  } = useAppSelector(state => state.relationship);
  const allStakeholderUserIds = useAppSelector(selectAllStakeholderUserIds);

  // Wait until sponsors are loaded and a real selection exists so we never fire
  // the empty user_ids "all accounts" request before My Portfolio defaults apply.
  const scopeReady =
    Boolean(assessmentStakeholders) &&
    !assessmentStakeholdersLoading &&
    selectedStakeholderUserIds.length > 0;

  const effectiveStakeholderUserIds = useMemo(
    () =>
      resolveEffectiveAccountUserIds(
        selectedStakeholderUserIds,
        viewMyPortfolio,
        { user_id: loggedInUserId, role: loggedInUser?.role }
      ),
    [
      loggedInUser?.role,
      loggedInUserId,
      selectedStakeholderUserIds,
      viewMyPortfolio,
    ]
  );

  const accountsUserIdsKey = useMemo(
    () =>
      buildRelationshipAccountsRequestKey(
        effectiveStakeholderUserIds,
        allStakeholderUserIds
      ),
    [allStakeholderUserIds, effectiveStakeholderUserIds]
  );

  const accountsInSync =
    scopeReady && relationshipAccountsUserIdsKey === accountsUserIdsKey;

  useEffect(() => {
    if (!scopeReady) return;
    // Already showing the correct scope — nothing to fetch.
    if (relationshipAccountsUserIdsKey === accountsUserIdsKey) return;

    dispatch(
      fetchRelationshipAssessmentAccounts({
        userIds: effectiveStakeholderUserIds,
      })
    );
    // Refetch when desired scope changes, or when store key drifts (stale response).
  }, [
    dispatch,
    scopeReady,
    accountsUserIdsKey,
    relationshipAccountsUserIdsKey,
    effectiveStakeholderUserIds,
  ]);

  const isScopeLoading =
    !scopeReady || relationshipAccountsLoading || (!accountsInSync && !error);

  const loadAccounts = useCallback(
    (force = false) => {
      if (!scopeReady && !force) return;
      dispatch(
        fetchRelationshipAssessmentAccounts({
          force,
          userIds: effectiveStakeholderUserIds,
        })
      );
    },
    [dispatch, effectiveStakeholderUserIds, scopeReady]
  );

  return {
    // Hide mismatched store data so a stale all-accounts response cannot paint.
    accounts: accountsInSync ? relationshipAccounts : [],
    accountsUserIdsKey,
    error,
    isScopeLoading,
    loadAccounts,
  };
}
